import { MemberType } from '@hc-frontend/entities'
import { REQUIRED_ERROR_MESSAGE } from 'messages'
import { alterDate, stringToDate } from '@hc-frontend/utils'
import curry from 'utils/functional/curry'
import {
  IS_SHORT_TERM,
  IS_FIXED_BENEFIT_MEDICAL,
  IS_ENROLL,
  IS_BRIDGE_TO_MEDICARE,
  IS_DENTAL_AND_VISION
} from 'core/constants/environment'

const GREATER_THAN_64_YEARS_11_MONTHS = IS_ENROLL
  ? 'We are not able to find plans that are suitable for you. Please call <a href="tel:+17139038544" onclick="dataLayerPushEvent(\'CALL_NO_COVERAGE_64_YEARS_11_MONTHS\')">713-903-8544</a> to speak with a licensed agent or visit Medicare.gov to find out about your coverage options.'
  : 'Must be less than 64 and 11 months years old.'

const GREATER_THAN_73_YEARS_11_MONTHS = IS_ENROLL
  ? 'We are not able to find plans that are suitable for you. Please call <a href="tel:+17139038544" onclick="dataLayerPushEvent(\'CALL_NO_COVERAGE_73_YEARS_11_MONTHS\')">713-903-8544</a> to speak with a licensed agent or visit Medicare.gov to find out about your coverage options.'
  : 'Must be less than 73 and 11 months years old.'

const LESS_THAN_60_YEARS = 'Must be at least 60 years old.'

const createDate = (config, date) => alterDate({ ...config, date }).getTime()

function greaterThan(dateToCompare, errorMessage) {
  return function innerGreaterThan(date, startDate) {
    return date < dateToCompare(startDate) ? errorMessage : ''
  }
}

function lessThan(dateToCompare, errorMessage) {
  return function innerLessThan(date, startDate) {
    return dateToCompare(startDate) < date ? errorMessage : ''
  }
}

const back64Years11Months = curry(createDate)({
  years: -65,
  months: 1,
  days: 2
})
const greaterThan64Years11Months = greaterThan(
  back64Years11Months,
  GREATER_THAN_64_YEARS_11_MONTHS
)

const back73Years11Months = curry(createDate)({
  years: -74,
  months: 1,
  days: 2
})
const greaterThan73Years11Months = greaterThan(
  back73Years11Months,
  GREATER_THAN_73_YEARS_11_MONTHS
)

const greaterThan18Years = greaterThan(
  curry(createDate)({ years: -18 }),
  'Must be less than 18 years old.'
)

const greaterThan100Years = greaterThan(
  curry(createDate)({ years: -100 }),
  'Must be less than 100 years old.'
)

const back60Years = curry(createDate)({ years: -60 })
const lessThan60Years = lessThan(back60Years, LESS_THAN_60_YEARS)

const lessThan18Years = lessThan(
  curry(createDate)({ years: -18 }),
  'Must be at least 18 years old.'
)

const lessThan30Days = lessThan(
  curry(createDate)({ days: -30 }),
  'Children under 30 days old are not eligible for coverage. Please remove this member.'
)

const greaterThan26Years = greaterThan(
  curry(createDate)({ years: -26, days: 2 }),
  'Children over 26 years old are not eligible for coverage. Please remove this member.'
)

const lessThanToday = lessThan(
  curry(createDate)({}),
  'Date of birth cannot be a future date.'
)

const lessThan6Months = lessThan(
  curry(createDate)({ months: -6 }),
  'Must be at least 6 months old.'
)

let rulesByTypeMember

setDOBRules()

function setDOBRules(coverageType = undefined, isChildOnly = false) {
  if (coverageType === 'stm' || IS_SHORT_TERM) {
    rulesByTypeMember = {
      [MemberType.Applicant]: isChildOnly
        ? [greaterThan18Years, lessThan6Months]
        : [greaterThan64Years11Months, lessThan18Years],
      [MemberType.Spouse]: [greaterThan64Years11Months, lessThan18Years],
      [MemberType.Child]: isChildOnly
        ? [greaterThan18Years, lessThan6Months]
        : [greaterThan26Years, lessThanToday]
    }
  }
  if (IS_ENROLL) {
    rulesByTypeMember = {
      [MemberType.Applicant]: [
        greaterThan100Years,
        greaterThan64Years11Months,
        lessThan6Months
      ],
      [MemberType.Spouse]: [greaterThan64Years11Months, lessThan18Years],
      [MemberType.Child]: [greaterThan26Years, lessThanToday]
    }
  }

  if (IS_FIXED_BENEFIT_MEDICAL || coverageType === 'fbm') {
    rulesByTypeMember = {
      [MemberType.Applicant]: [greaterThan73Years11Months, lessThan18Years],
      [MemberType.Spouse]: [greaterThan73Years11Months, lessThan18Years],
      [MemberType.Child]: isChildOnly
        ? [greaterThan18Years, lessThan30Days]
        : [greaterThan26Years, lessThan30Days]
    }
  }

  if (IS_BRIDGE_TO_MEDICARE) {
    rulesByTypeMember = {
      [MemberType.Applicant]: [greaterThan64Years11Months, lessThan60Years],
      [MemberType.Spouse]: [greaterThan64Years11Months, lessThan18Years],
      [MemberType.Child]: [greaterThan26Years, lessThan6Months]
    }
  }

  if (IS_DENTAL_AND_VISION) {
    rulesByTypeMember = {
      [MemberType.Applicant]: [greaterThan100Years, lessThan18Years],
      [MemberType.Spouse]: [greaterThan100Years, lessThan18Years],
      [MemberType.Child]: [greaterThan26Years, lessThanToday]
    }
  }
}

function getErrorToDOB(typeMember, date, startDate) {
  date = stringToDate(date)
  startDate = stringToDate(startDate)

  if (!date) return REQUIRED_ERROR_MESSAGE

  date = date.getTime()

  return rulesByTypeMember[typeMember].reduce(
    (error, rule) => error || rule(date, startDate),
    ''
  )
}

export default getErrorToDOB
export { back64Years11Months, back60Years, GREATER_THAN_64_YEARS_11_MONTHS, setDOBRules }
